import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { formValueSelector, change, clearFields } from 'redux-form';

import { BaseCrud, FormSection, FormSectionColumn } from 'react/containers/layout/Crud';
import { TextField, LookupField, CheckboxField, IntegerField, RadioGroupField } from 'react/components/fields';

const renderGeneralSection = ({
  fieldEnabled,
  organizationField,
  groupField,
  changeAction
}) => {

  useEffect(() => {
    organizationLookupDisabled();
  });

  const organizationFieldRef = useRef(null);
  const groupFieldRef = useRef(null);

  const chooseEnabledRadioButton = () => {
    organizationLookupDisabled();

    if (!!organizationField) {
      return 'organizationEnabled';
    }

    if (!!groupField) {
      return 'groupEnabled';
    }
  };

  const organizationLookupDisabled = () => {
    if (fieldEnabled === 'organizationEnabled') {
      organizationFieldRef.current?.state?.monetField?.enable();
      groupFieldRef.current?.state?.monetField?.disable();
      changeAction('SecurityPolicy', 'group', null);
    }

    if (fieldEnabled === 'groupEnabled') {
      groupFieldRef.current?.state?.monetField?.enable();
      organizationFieldRef.current?.state?.monetField?.disable();
      changeAction('SecurityPolicy', 'organization', null);
    }
  }

  return (
    <FormSection
      title="SecurityPolicy.section.general.title"
      classname="generalSection"
    >
      <FormSectionColumn>
        <div className="description">
          <TextField
            label="securityPolicy.field.description"
            name="description"
            required
          />
        </div>
        <div className="alternativeFields">
          <RadioGroupField
            label="securityPolicy.organization.enabled"
            name="fieldEnabled"
            defaultValue="organizationEnabled"
            value={fieldEnabled || chooseEnabledRadioButton()}
            doNotShowOnGrid
            options={[
              {
                label: "securityPolicy.field.organization",
                value: 'organizationEnabled'
              },
              {
                label: "securityPolicy.field.group",
                value: 'groupEnabled'
              }
            ]}
          />
          <div className="lookupFields">
            <LookupField
              ref={organizationFieldRef}
              name="organization"
              label="securityPolicy.field.organization"
              lookupEntity="Organization"
              lookupEntityColumn="description"
              required={fieldEnabled === 'organizationEnabled'}
              disabled={fieldEnabled === 'groupEnabled'}
            />
            <LookupField
              ref={groupFieldRef}
              label="securityPolicy.field.group"
              name="group"
              lookupEntity="Group"
              lookupEntityColumn="name"
              required={fieldEnabled === 'groupEnabled'}
              disabled={fieldEnabled === 'organizationEnabled'}
            />
          </div>
        </div>
      </FormSectionColumn>
    </FormSection>
  );
}

const renderTimeoutPolicySection = ({
  enableField,
  minutesToTimeoutField,
  changeAction
}) => {

  const handleEnableChange = () => {
    changeAction('SecurityPolicy', 'timeout', '')
  }

  return (
    <FormSection title="TimeoutPolicy.section.general.title">
      <FormSectionColumn>
        <CheckboxField
          label="timeoutPolicy.field.enabled"
          name="enabled"
          doNotShowOnGrid
          defaultValue={false}
          onChange={handleEnableChange}
          value={enableField || !!minutesToTimeoutField}
        />
        <IntegerField
          label="timeoutPolicy.minutesToTimeout"
          name="timeout"
          type="TOGGLENUMBER_RANGE"
          min={0}
          max={60}
          shouldShow={!!enableField}
          defaultValue={minutesToTimeoutField}
          required
          customValidation={min2MinuteValidation}
        />
      </FormSectionColumn>
    </FormSection>
  );
}

const min2MinuteValidation = (value) => {

  if (value <= 1) {
    return Monet.I18n.getResourcesByKey('SecurityPolicy.error.mustBeGreaterThan1');
  }

  return undefined;
}

const beforeSubmit = (values) => {
  const { organization, group } = values;

  delete values.enabled;
  delete values.fieldEnabled;

  if (organization === null) {
    delete values.organization;
  } else {
    const { id } = organization;

    values.organization = {
      id: parseInt(id)
    }
  }

  if (group === null) {
    delete values.group;
  } else {
    const { id } = group;

    values.group = {
      id: parseInt(id)
    }
  }

  return values;
};

const SecurityPolicyCrud = (props) => {

  return (
    <BaseCrud
      updateMethod="PUT"
      tabName="SecurityPolicy.title"
      beforeSubmit={beforeSubmit}
      {...props}
    >
      {renderGeneralSection(props)}
      {renderTimeoutPolicySection(props)}
    </BaseCrud>
  )
}

const selector = formValueSelector('SecurityPolicy');

const mapStateToProps = (state) => ({
  fieldEnabled: selector(state, 'fieldEnabled'),
  organizationField: selector(state, 'organization'),
  groupField: selector(state, 'group'),
  enableField: selector(state, 'enabled'),
  minutesToTimeoutField: selector(state, 'timeout')
});

const mapDispatchToProps = {
  changeAction: change,
  clearFieldsAction: clearFields
};

export default connect(mapStateToProps, mapDispatchToProps)(SecurityPolicyCrud);
